import { useFitnessResource, notifyFitnessChanged } from "@/hooks/useFitnessResource";
import { comparableBodyMeasurements, readBodyHealthRecordGuards, withBodyHealthRecordGuards } from '@/lib/bodyHealthRecordGuards';
import { BODY_HEALTH_CHANGED_EVENT, isBodyHealthChangeFor } from '@/lib/bodyHealthChanges';
import { readFitnessHistory, localFitnessDay } from "@/lib/fitnessData";
import FitnessDataState from "@/components/fitness/FitnessDataState";
import ExerciseProgressPanel from "@/components/fitness/ExerciseProgressPanel";
// FitnessProgress — 100% data-driven progress for the Fitness core module.
// Three sections (Treino / Corpo / Consistência) + the workout reminder.
// Everything renders ONLY from data the user actually logged; a section with
// no data shows an empty state with a CTA to log it — never an invented metric.
//
// Removed vs the previous version (diagnosis, PR report):
// - Old personal records queried workout_exercises; the exercise progress panel
//   now reads completed, account-owned workout_sessions and working sets.
// - Editable weight/height inputs: weight is canonical in body_measurements
//   now (unified); the Corpo section reads it and links to Medidas.
// - Wearable "coming soon" placeholder: dead card; connections live in
//   Settings → Connections.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import { ChevronLeft, TrendingUp, Dumbbell, Clock, Ruler } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getGymVolumeKg, getSessionKind, type WorkoutSessionRow } from "@/lib/fitness/sessionKind";
import type { BodyMeasurement } from "@/hooks/useBodyMeasurements";
import { EvolutionChart } from "@/components/charts/EvolutionChart";
import { AnalyticsDashboard } from "@/components/analytics/AnalyticsDashboard";
import { buildDashboard } from "@/lib/analytics/dashboard";
import { FITNESS_DASHBOARD, extractFitnessSamples } from "@/lib/analytics/fitnessDashboard";
import type { Granularity } from "@/lib/analytics/engine";

type PeriodDays = 30 | 90 | 365;

const BODY_SERIES = [
  { field: "weight_kg", color: "hsl(187 72% 50%)", labelKey: "fitness.bodyScan.legendWeight" },
  { field: "waist_cm", color: "hsl(265 60% 65%)", labelKey: "fitness.bodyScan.legendWaist" },
  { field: "body_fat_pct", color: "hsl(30 90% 55%)", labelKey: "fitness.bodyScan.legendBodyFat" },
] as const;

const card = (borderColor = "hsl(187 72% 50% / 0.15)") => ({
  background: "hsl(var(--card))",
  border: `1px solid ${borderColor}`,
});

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3" style={{ color: "hsl(187 72% 50% / 0.45)" }}>{children}</p>
);

const EmptyCta: React.FC<{ text: string; cta: string; onClick: () => void }> = ({ text, cta, onClick }) => (
  <div className="text-center py-4">
    <p className="text-[12px] font-body text-foreground/45 mb-3">{text}</p>
    <button
      onClick={onClick}
      className="px-4 py-2 rounded-xl font-display text-[12px] font-bold"
      style={{ background: "hsl(187 72% 50% / 0.15)", border: "1px solid hsl(187 72% 50% / 0.4)", color: "hsl(187 72% 60%)" }}
    >
      {cta}
    </button>
  </div>
);

async function readProgress(userId: string, assertCurrent: () => void) {
  const sessions = await readFitnessHistory(userId, assertCurrent);
  const { data: profile, error } = await supabase.from("fitness_profiles").select("id,workout_reminder_enabled,workout_reminder_time").eq("user_id", userId).maybeSingle();
  assertCurrent(); if (error) throw error;
  const measurements: BodyMeasurement[] = [];
  for (let offset = 0; ; offset += 500) {
    const result = await supabase.from("body_measurements").select("*").eq("user_id", userId).lte("measured_at", new Date().toISOString()).order("measured_at", { ascending: false }).order("id", { ascending: false }).range(offset, offset + 499);
    assertCurrent(); if (result.error || !Array.isArray(result.data)) throw result.error ?? new Error("Missing measurements");
    if (result.data.some(row => row.user_id !== userId)) throw new Error('Body progress owner changed');
    measurements.push(...result.data as BodyMeasurement[]); if (result.data.length < 500) break;
  }
  const guards = await readBodyHealthRecordGuards(userId, 'body', measurements.map(row => row.id), { assertCurrent });
  assertCurrent();
  return { sessions, profile, measurements: withBodyHealthRecordGuards(measurements, guards) };
}

const FitnessProgress: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();


  const resource = useFitnessResource(readProgress);
  const sessions = useMemo(() => resource.data?.sessions ?? [], [resource.data]);
  const measurements = useMemo(() => resource.data?.measurements ?? [], [resource.data]);
  const progressOwner = resource.userId, refreshProgress = resource.refresh;
  useEffect(() => {
    const changed = (event: Event) => { if (isBodyHealthChangeFor(event, progressOwner, 'body')) void refreshProgress(); };
    window.addEventListener(BODY_HEALTH_CHANGED_EVENT, changed);
    return () => window.removeEventListener(BODY_HEALTH_CHANGED_EVENT, changed);
  }, [progressOwner, refreshProgress]);
  const loading = resource.status !== "ready";
  const [savingReminder, setSavingReminder] = useState(false);
  const reminderLock = useRef(false);
  const reminderDirty = useRef(false);
  const [period, setPeriod] = useState<PeriodDays>(30);
  const [granularity, setGranularity] = useState<Granularity>(FITNESS_DASHBOARD.defaultGranularity);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState("07:00");

  useEffect(() => { reminderDirty.current = false; setReminderEnabled(false); setReminderTime("07:00"); setSavingReminder(false); }, [resource.userId]);
  useEffect(() => {
    if (!reminderDirty.current && resource.data?.profile) {
      setReminderEnabled(!!resource.data.profile.workout_reminder_enabled);
      setReminderTime((resource.data.profile.workout_reminder_time || "07:00").slice(0, 5));
    }
  }, [resource.data]);

  const cutoffMs = Date.now() - period * 86_400_000;
  const inPeriod = useMemo(
    () => sessions.filter((s) => s.completed_at && new Date(s.completed_at).getTime() >= cutoffMs),
    [sessions, cutoffMs],
  );

  // ── TREINO stats (all from real workout_sessions rows) ────────────────────
  const trainStats = useMemo(() => {
    const total = inPeriod.length;
    const weeks = Math.max(1, period / 7);
    const perWeek = total / weeks;
    const totalMin = inPeriod.reduce((a, s) => a + (s.duration_minutes || 0), 0);
    const avgMin = total > 0 ? Math.round(totalMin / total) : 0;
    const volume = inPeriod.reduce((sum, session) => sum + getGymVolumeKg(session), 0);
    // Distribution by origin — data that exists on every row: plan_id (Livre
    // vs Plano) + source (imported activities).
    let free = 0, plan = 0, imported = 0;
    for (const s of inPeriod) {
      if (getSessionKind(s) === "activity") imported++;
      else if (s.plan_id) plan++;
      else free++;
    }
    return { total, perWeek, totalMin, avgMin, volume, dist: { free, plan, imported } };
  }, [inPeriod, period]);

  // ── EVOLUÇÃO — shared analytics engine (day/week/month + trend + streak +
  // projection). The workout-day STREAK now lives here via ONE canonical
  // definition (src/lib/analytics/engine.computeStreak), replacing the old
  // inline copy above. Period-independent: computed on the complete paginated history.
  const analytics = useMemo(
    () => buildDashboard(extractFitnessSamples(sessions), FITNESS_DASHBOARD, granularity),
    [sessions, granularity],
  );

  // ── CORPO series (canonical body_measurements) ────────────────────────────
  const comparisonRows = useMemo(() => comparableBodyMeasurements(measurements), [measurements]);
  const bodySeries = useMemo(() => {
    const rows = [...comparisonRows]
      .filter((m) => new Date(m.measured_at).getTime() >= cutoffMs)
      .sort((a, b) => new Date(a.measured_at).getTime() - new Date(b.measured_at).getTime());
    return BODY_SERIES.map((s) => ({
      key: s.field,
      color: s.color,
      label: t(s.labelKey),
      values: rows
        .map((m) => ({ t: new Date(m.measured_at).getTime(), v: (m as Record<string, unknown>)[s.field] }))
        .filter((p): p is { t: number; v: number } => typeof p.v === "number"),
    }));
  }, [comparisonRows, cutoffMs, t]);
  const latestBody = measurements[0] ?? null;
  const prevBody = comparisonRows[1] ?? null;
  const hasBodyChart = bodySeries.some((s) => s.values.length >= 2);

  // ── CONSISTÊNCIA heatmap (last min(period, 90) days, week columns) ────────
  const heatmap = useMemo(() => {
    const days = Math.min(period, 90);
    const done = new Set(inPeriod.map((s) => localFitnessDay(new Date(s.completed_at!))));
    const cells: Array<{ iso: string; active: boolean }> = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const iso = localFitnessDay(d);
      cells.push({ iso, active: done.has(iso) });
    }
    const weeks: Array<typeof cells> = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }, [inPeriod, period]);

  const saveReminder = async () => {
    if (reminderLock.current || !/^([01]\d|2[0-3]):[0-5]\d$/.test(reminderTime)) return;
    const current = resource.capture(); if (!current()) return;
    reminderLock.current = true; setSavingReminder(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !current() || user?.id !== resource.userId) throw authError ?? new Error("Fitness account changed");
      const { data, error } = await supabase.from("fitness_profiles").update({ workout_reminder_enabled: reminderEnabled, workout_reminder_time: reminderTime }).eq("user_id", user.id).eq("id", resource.data?.profile?.id).select("id");
      if (!current()) return;
      if (error || data?.length !== 1 || data[0].id !== resource.data?.profile?.id) throw error ?? new Error("Reminder save not confirmed");
      reminderDirty.current = false; notifyFitnessChanged(); toast.success(t("fitness.reminder.saved"));
    } catch (err) {
      if (current()) toast.error(t("fitness.progress.reminderError"));
    } finally { reminderLock.current = false; if (current()) setSavingReminder(false); }
  };

  const stagger = (i: number) => ({ delay: 0.06 + i * 0.07, duration: 0.5 });
  const fmtVol = (v: number) => (v < 1000 ? `${Math.round(v)}kg` : `${(v / 1000).toFixed(1).replace(/\.0$/, "")}k`);

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]" style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }} /></div>

      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-400 mb-4 active:opacity-70"><ChevronLeft size={16} /><span className="text-[13px] font-display">{t("common.back")}</span></button>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
        <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("fitness.title")}</p>
        <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">{t("fitness.progress.title")}</h1>
      </motion.div>

      {/* Period pills — drive Treino, Corpo and Consistência together */}
      <div className="flex gap-2 mb-5">
        {([30, 90, 365] as PeriodDays[]).map((d) => (
          <button key={d} onClick={() => setPeriod(d)}
            aria-pressed={period === d} className="min-h-11 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={period === d
              ? { background: "hsl(187 72% 50% / 0.18)", color: "hsl(187 72% 60%)", border: "1px solid hsl(187 72% 50% / 0.4)" }
              : { background: "hsl(222 18% 12%)", color: "hsl(222 10% 60%)", border: "1px solid hsl(222 18% 20%)" }}>
            {t(d === 30 ? "fitness.bodyScan.period30" : d === 90 ? "fitness.bodyScan.period90" : "fitness.bodyScan.period365")}
          </button>
        ))}
      </div>

      {loading ? <FitnessDataState status={resource.status} busy={resource.isRefreshing} onRetry={resource.refresh} /> : (
        <>
          {/* ── TREINO ── */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(0)} className="rounded-2xl p-4 mb-4" style={card()}>
            <SectionTitle>{t("fitness.progress.section.training")}</SectionTitle>
            {trainStats.total === 0 ? (
              <EmptyCta text={t("fitness.progress.training.empty")} cta={t("fitness.progress.training.cta")} onClick={() => navigate("/fitness/free-workout")} />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {[
                    { icon: Dumbbell, label: t("fitness.progress.totalWorkouts"), value: String(trainStats.total), color: "hsl(187 72% 55%)" },
                    { icon: TrendingUp, label: t("fitness.progress.perWeek"), value: trainStats.perWeek.toFixed(1), color: "hsl(140 60% 55%)" },
                  ].map((s, i) => (
                    <div key={i} className="rounded-xl p-3 text-center" style={{ background: "hsl(222 18% 12%)", border: `1px solid ${s.color}22` }}>
                      <s.icon size={13} className="mx-auto mb-1" style={{ color: s.color }} />
                      <p className="font-display text-[18px] font-bold text-foreground">{s.value}</p>
                      <p className="text-[8px] font-display tracking-wide" style={{ color: "hsl(218 14% 45%)" }}>{s.label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-[11px] font-display text-foreground/60 mb-3">
                  <span><Clock size={10} className="inline mr-1" />{t("fitness.progress.totalTime")}: <b className="text-foreground">{Math.floor(trainStats.totalMin / 60)}h {trainStats.totalMin % 60}m</b></span>
                  <span>{t("fitness.progress.avgDuration")}: <b className="text-foreground">{trainStats.avgMin} min</b></span>
                  {trainStats.volume > 0 && <span>{t("fitness.progress.volume")}: <b className="text-foreground">{fmtVol(trainStats.volume)}</b></span>}
                </div>
                {/* Distribution by origin — only rendered when >1 kind exists */}
                {[trainStats.dist.free, trainStats.dist.plan, trainStats.dist.imported].filter((n) => n > 0).length > 1 && (
                  <div className="space-y-1.5">
                    {[
                      { label: t("fitness.progress.dist.free"), n: trainStats.dist.free, color: "hsl(187 72% 50%)" },
                      { label: t("fitness.progress.dist.plan"), n: trainStats.dist.plan, color: "hsl(265 60% 60%)" },
                      { label: t("fitness.progress.dist.imported"), n: trainStats.dist.imported, color: "hsl(20 90% 55%)" },
                    ].filter((d) => d.n > 0).map((d) => (
                      <div key={d.label} className="flex items-center gap-2">
                        <span className="text-[10px] font-display w-20 shrink-0" style={{ color: "hsl(218 14% 55%)" }}>{d.label}</span>
                        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "hsl(222 18% 14%)" }}>
                          <div className="h-full rounded-full" style={{ width: `${(d.n / trainStats.total) * 100}%`, background: d.color }} />
                        </div>
                        <span className="text-[10px] font-display font-bold text-foreground w-6 text-right">{d.n}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </motion.div>

          {/* ── EVOLUÇÃO — shared analytics engine (day/week/month + trend +
              streak + projection). Period-independent: always the complete paginated
              history; its own day/week/month toggle is separate from the pills
              above. Descriptive only (no prescription — guardrail). ── */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(1)} className="rounded-2xl p-4 mb-4" style={card()}>
            <SectionTitle>{t("fitness.analytics.title")}</SectionTitle>
            <AnalyticsDashboard
              results={analytics}
              granularity={granularity}
              onGranularityChange={setGranularity}
              emptyKey="fitness.analytics.empty"
            />
          </motion.div>

          <ExerciseProgressPanel key={resource.userId} ownerId={resource.userId!} sessions={sessions} asOf={resource.checkedAt ?? undefined} />

          {/* ── CORPO — canonical body_measurements ── */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(1)} className="rounded-2xl p-4 mb-4" style={card()}>
            <div className="flex items-center justify-between mb-1">
              <SectionTitle>{t("fitness.progress.section.body")}</SectionTitle>
              <button onClick={() => navigate("/fitness/body-scan")} className="text-[10px] font-display font-bold flex items-center gap-1" style={{ color: "hsl(187 72% 60%)" }}>
                <Ruler size={11} />{t("fitness.progress.body.open")}
              </button>
            </div>
            {latestBody?.user_corrected && <p className="mb-3 text-sm text-muted-foreground">{t("bodyHealthAction.userCorrected")}</p>}
            {!latestBody ? (
              <EmptyCta text={t("fitness.progress.body.empty")} cta={t("fitness.progress.body.cta")} onClick={() => navigate("/fitness/body-scan")} />
            ) : (
              <>
                <div className="flex gap-4 mb-3 text-[12px] font-display text-foreground/70">
                  {typeof latestBody.weight_kg === "number" && (
                    <span>{t("fitness.bodyScan.legendWeight")}: <b className="text-foreground">{latestBody.weight_kg}kg</b>{prevBody && typeof prevBody.weight_kg === "number" && <Delta d={latestBody.weight_kg - prevBody.weight_kg} unit="kg" />}</span>
                  )}
                  {typeof latestBody.waist_cm === "number" && (
                    <span>{t("fitness.bodyScan.legendWaist")}: <b className="text-foreground">{latestBody.waist_cm}cm</b>{prevBody && typeof prevBody.waist_cm === "number" && <Delta d={latestBody.waist_cm - prevBody.waist_cm} unit="cm" />}</span>
                  )}
                  {typeof latestBody.body_fat_pct === "number" && (
                    <span>{t("fitness.bodyScan.legendBodyFat")}: <b className="text-foreground">{latestBody.body_fat_pct}%</b></span>
                  )}
                </div>
                {hasBodyChart
                  ? <EvolutionChart series={bodySeries} />
                  : <p className="text-[11px] text-muted-foreground">{t("fitness.bodyScan.chartEmpty")}</p>}
              </>
            )}
          </motion.div>

          {/* ── CONSISTÊNCIA — day heatmap ── */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(2)} className="rounded-2xl p-4 mb-4" style={card()}>
            <SectionTitle>{t("fitness.progress.section.consistency")}</SectionTitle>
            {inPeriod.length === 0 ? (
              <EmptyCta text={t("fitness.progress.training.empty")} cta={t("fitness.progress.training.cta")} onClick={() => navigate("/fitness/free-workout")} />
            ) : (
              <div className="flex gap-1 overflow-x-auto pb-1">
                {heatmap.map((week, wi) => (
                  <div key={wi} className="flex flex-col gap-1">
                    {week.map((cell) => (
                      <div key={cell.iso} title={cell.iso} className="w-3 h-3 rounded-[3px]"
                        style={{ background: cell.active ? "hsl(187 72% 50%)" : "hsl(222 18% 14%)" }} />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* ── Workout Reminder (preserved) ── */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(3)} className="rounded-2xl p-4 mb-5" style={card()}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[9px] font-display tracking-[0.18em] uppercase" style={{ color: "hsl(187 72% 50% / 0.45)" }}>{t("fitness.reminder.title")}</p>
              <button type="button" role="switch" aria-checked={reminderEnabled} aria-label={t("fitness.reminder.title")} disabled={savingReminder} onClick={() => { reminderDirty.current = true; setReminderEnabled(!reminderEnabled); }}
                className="w-14 h-11 rounded-full transition-all relative" style={{ background: reminderEnabled ? "hsl(187 72% 45%)" : "hsl(218 14% 20%)" }}>
                <div className="w-4 h-4 rounded-full bg-white absolute top-3.5 transition-all" style={{ left: reminderEnabled ? "22px" : "2px" }} />
              </button>
            </div>
            {reminderEnabled && (
              <input type="time" disabled={savingReminder} aria-label={t("fitness.reminder.title")} value={reminderTime} onChange={e => { reminderDirty.current = true; setReminderTime(e.target.value); }}
                className="w-full rounded-xl px-3 py-2 font-display text-[13px] font-semibold text-foreground outline-none"
                style={{ background: "hsl(222 18% 12%)", border: "1px solid hsl(222 18% 18%)", colorScheme: "dark" }} />
            )}
            <button type="button" disabled={savingReminder || !resource.data?.profile || !/^([01]\d|2[0-3]):[0-5]\d$/.test(reminderTime)} onClick={saveReminder} className="mt-3 min-h-11 w-full rounded-xl border border-primary/40 text-primary disabled:opacity-50">{t(savingReminder ? "agenda.saving" : "fitness.history.edit.save")}</button>
          </motion.div>
        </>
      )}
    </MobileLayout>
  );
};

// Signed inline delta — down = cyan, up = neutral; the Mirror interprets, not the UI.
const Delta: React.FC<{ d: number; unit: string }> = ({ d, unit }) => {
  const r = Math.round(d * 10) / 10;
  if (r === 0) return null;
  return (
    <span className="ml-1 text-[10px] font-bold" style={{ color: r < 0 ? "hsl(187 72% 60%)" : "hsl(222 10% 65%)" }}>
      {r > 0 ? "+" : ""}{r}{unit}
    </span>
  );
};

export default FitnessProgress;
